import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  BookOpen,
  Download,
  ArrowRight,
  ArrowUpRight,
  FileText,
  Calendar,
  Tag,
  Shield,
  Scale,
  Landmark,
  Globe,
  Users,
  Receipt,
  Building2,
  Cpu,
  Briefcase,
  Factory,
  Store,
  Wallet,
  HandCoins,
  Gift,
  Home,
  Award,
  Leaf,
  ShieldAlert,
  Heart,
  Plane,
  KeyRound,
  FilePlus2,
  Workflow,
  Library,
  BookMarked,
  Banknote,
} from 'lucide-react';
import { Button } from './ui/button';

/**
 * Publications page — RST Compliance Manual.
 *
 * Chapter heads here mirror the firm's actual 2022 manual (26 sections),
 * plus a new forward-looking head ("Data Privacy & AI Governance")
 * being added for the 2026 / 2027 editions.
 */

const COMPLIANCE_HEADS = [
  {
    id: 'income-tax',
    no: '01',
    icon: Scale,
    title: 'Income Tax',
    file: '/downloads/compliance-01-income-tax.pdf',
    summary:
      'Tax rates, advance tax, TDS/TCS, ITR filing, tax audit, presumptive taxation, depreciation, retirement benefits, Chapter VI-A, and 115BAA / 115BAB.',
  },
  {
    id: 'demonetisation',
    no: '02',
    icon: Wallet,
    title: 'Demonetisation',
    file: '/downloads/compliance-02-demonetisation.pdf',
    summary:
      'Concept, global examples, applicability, compliances, and key cash-transaction restrictions for businesses.',
  },
  {
    id: 'factories-act',
    no: '03',
    icon: Factory,
    title: 'Factories Act, 1948',
    file: '/downloads/compliance-03-factories-act-to-be-subsumed-with-new-labor-code.pdf',
    summary:
      'Applicability, registration & licensing, books and registers, return filings, and overtime / women-worker exemptions.',
  },
  {
    id: 'karnataka-shops',
    no: '04',
    icon: Store,
    title: 'Karnataka Shops & Commercial Establishments Act, 1961',
    file: '/downloads/compliance-04-section-4-karnataka-shops-commercial-establishment-act-1961.pdf',
    summary:
      'Registration, renewals, important registers, prohibited activities, operating-hour and signage rules.',
  },
  {
    id: 'payment-of-wages',
    no: '05',
    icon: HandCoins,
    title: 'Payment of Wages Act',
    file: '/downloads/compliance-05-payment-of-wages-act.pdf',
    summary:
      'Applicability, books to be maintained, return filings, disbursement timelines, and penalties for contravention.',
  },
  {
    id: 'professional-tax',
    no: '06',
    icon: Receipt,
    title: 'Professional Tax',
    file: '/downloads/compliance-06-professional-tax.pdf',
    summary:
      'Liability, exemptions, Karnataka rate chart, monthly/quarterly due dates, and consequences of default.',
  },
  {
    id: 'provident-fund',
    no: '07',
    icon: Wallet,
    title: 'Provident Fund (PF)',
    file: '/downloads/compliance-07-provident-fund-pf.pdf',
    summary:
      'Applicability, due dates for filing & payment, EPF/EPS/EDLI/admin charges chart, and default interest.',
  },
  {
    id: 'esi',
    no: '08',
    icon: Heart,
    title: 'Employee State Insurance (ESI)',
    file: '/downloads/compliance-08-employee-state-insurance-esi.pdf',
    summary:
      'Applicability, payment & filing due dates, contribution chart, record maintenance, and default penalties.',
  },
  {
    id: 'bonus',
    no: '09',
    icon: Gift,
    title: 'Payment of Bonus Act, 1965',
    file: '/downloads/compliance-09-bonus-payment-of-bonus-act-1965.pdf',
    summary:
      'Eligibility, salary components for bonus computation, rate, time limits, registers, and default consequences.',
  },
  {
    id: 'gratuity',
    no: '10',
    icon: Award,
    title: 'Payment of Gratuity Act, 1972',
    file: '/downloads/compliance-10-gratuity-payment-of-gratuity-act-1972.pdf',
    summary:
      'Applicability, eligibility, gratuity computation, maximum limit, and prescribed forms.',
  },
  {
    id: 'rera',
    no: '11',
    icon: Home,
    title: 'RERA, 2016',
    file: '/downloads/compliance-11-real-estate-regulation-and-development-act-2016.pdf',
    summary:
      'Mandatory registration, ongoing-project rules, fees, quarterly compliance, complaints, and penalties.',
  },
  {
    id: 'msme',
    no: '12',
    icon: Briefcase,
    title: 'MSME — Micro, Small & Medium Enterprises',
    file: '/downloads/compliance-12-micro-small-and-medium-enterprises.pdf',
    summary:
      'Classification, Udyam registration, benefits, 25+ MSME schemes (PMEGP, CGTMSE, ZED, ASPIRE, CLCSS, …), and buyer-supplier payment provisions.',
  },
  {
    id: 'sez',
    no: '13',
    icon: Globe,
    title: 'Special Economic Zone (SEZ)',
    file: '/downloads/compliance-13-special-economic-zone.pdf',
    summary:
      'SEZ unit & developer benefits, income-tax exemptions, applications, reports, returns, and customs amendments.',
  },
  {
    id: 'kspcb',
    no: '14',
    icon: Leaf,
    title: 'Karnataka State Pollution Control Board (KSPCB)',
    file: '/downloads/compliance-14-karnataka-state-pollution-control-board-kspcb.pdf',
    summary:
      'Industry-category applicability, forms for E-Waste and Batteries Management & Handling Rules.',
  },
  {
    id: 'gst',
    no: '15',
    icon: Receipt,
    title: 'Goods & Services Tax (GST)',
    file: '/downloads/compliance-15-goods-service-tax-act-gst.pdf',
    summary:
      'Registration thresholds, record & books-of-accounts maintenance, due dates & forms, invoicing, e-way bill, and prosecution.',
  },
  {
    id: 'company-law',
    no: '16',
    icon: Building2,
    title: 'Company Law (Companies Act, 2013 & 1956)',
    file: '/downloads/compliance-16-company-law-matters.pdf',
    summary:
      'Incorporation forms, post-incorporation events, AGM, annual ROC filings, and a full penalties table.',
  },
  {
    id: 'csr',
    no: '17',
    icon: Heart,
    title: 'Corporate Social Responsibility (CSR)',
    file: '/downloads/compliance-17-corporate-social-responsibility.pdf',
    summary:
      'Applicability thresholds (turnover / net worth / net profit), eligible activities, and tax treatment of CSR spend.',
  },
  {
    id: 'llp',
    no: '18',
    icon: Building2,
    title: 'Limited Liability Partnership (LLP)',
    file: '/downloads/compliance-18-limited-liability-partnership.pdf',
    summary:
      'Incorporation documents, post-incorporation forms, fees schedule, statutory & tax audit, and ROC annual filings.',
  },
  {
    id: 'ibc',
    no: '19',
    icon: ShieldAlert,
    title: 'Insolvency and Bankruptcy Code, 2016',
    file: '/downloads/compliance-19-insolvency-and-bankruptcy-code-2016.pdf',
    summary:
      'Code objectives, applicability, resolution triggers, creditor categories, CIRP for companies, and individual / partnership processes.',
  },
  {
    id: 'trust',
    no: '20',
    icon: Library,
    title: 'Trusts (Indian Trust Act)',
    file: '/downloads/compliance-20-trust-indian-trust-act.pdf',
    summary:
      'Public charitable activities, registration forms, exemptions u/s 11 and conditions, and key Income-Tax Act sections for trusts.',
  },
  {
    id: 'iec',
    no: '21',
    icon: Plane,
    title: 'Import Export Code (IEC)',
    file: '/downloads/compliance-21-import-export-code-iec.pdf',
    summary:
      'IEC applicability, documents required, where to file, and exemptions for personal-use and neighbouring-country trade.',
  },
  {
    id: 'digital-signatures',
    no: '22',
    icon: KeyRound,
    title: 'Digital Signatures',
    file: '/downloads/compliance-22-digital-signatures.pdf',
    summary:
      'Features (integrity, authentication, non-repudiation), and Class I / II / III digital signatures with their use-cases.',
  },
  {
    id: 'it-forms',
    no: '23',
    icon: FilePlus2,
    title: 'Forms under the Income Tax Act',
    file: '/downloads/compliance-23-forms-prescribed-under-income-tax-act.pdf',
    summary:
      'Tax challans, charitable & religious trust forms, PAN, TAN and other commonly used Income-Tax Act forms.',
  },
  {
    id: 'sops',
    no: '24',
    icon: Workflow,
    title: 'Standard Operating Procedures (SOPs)',
    file: '/downloads/compliance-24-sops-standard-operating-procedures.pdf',
    summary:
      'Process flows for IT filing, TDS filing, GST filing, TDS-return correction, registration compliances, company registration and notice-reply.',
  },
  {
    id: 'annexures',
    no: '25',
    icon: BookMarked,
    title: 'Annexures — IT Computation, TDS / TCS, PT Rates',
    file: '/downloads/compliance-25-annexures.pdf',
    summary:
      'Computation templates (salary, house property, capital gains), Cost Inflation Index, TDS/TCS rates, and Karnataka Professional Tax rates.',
  },
  {
    id: 'references',
    no: '26',
    icon: Banknote,
    title: 'References',
    file: '/downloads/compliance-26-references.pdf',
    summary:
      'Curated list of government department and regulatory body web resources used across the manual.',
  },
  // Forward-looking head added for the 2026 / 2027 editions
  {
    id: 'dpdpa-ai',
    no: '27',
    icon: Cpu,
    title: 'Data Privacy (DPDPA) & AI Governance',
    file: null,
    summary:
      'DPDPA 2023 readiness, consent-manager workflows, breach notification, AI/ML model governance and Responsible AI policies.',
    isNew: true,
  },
];

const EDITIONS = [
  {
    year: '2024',
    label: 'Edition 2024 · Released',
    tone: 'released',
    description:
      'Published edition — comprehensive compliance reference across 26 chapters covering all major Indian regulatory regimes.',
    fileUrl: '/downloads/rst-compliance-manual-2024-full.pdf',
  },
  {
    year: '2026',
    label: 'Edition 2026 · Under update',
    tone: 'updated',
    description:
      'Refresh in progress — covering Union Budget 2026-27, GST 2.0, DPDPA Rules, the Labour Codes, and the Income-Tax Act, 2025 transition.',
    fileUrl: null,
  },
  {
    year: '2027',
    label: 'Edition 2027 · Forthcoming',
    tone: 'forthcoming',
    description:
      'Forward-looking edition aligned to AY 2027-28 (new Income-Tax Act rollout), India AI Act readiness, and post-Labour-Codes implementation.',
    fileUrl: null,
  },
];

const PublicationsPage = () => {
  const [activeEdition, setActiveEdition] = useState('2024');
  const navigate = useNavigate();
  const currentEdition = EDITIONS.find((e) => e.year === activeEdition);
  const isDownloadable = Boolean(currentEdition?.fileUrl);

  const goToContact = () => {
    navigate('/');
    setTimeout(() => {
      const el = document.getElementById('cta');
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 120);
  };

  return (
    <div className="min-h-screen bg-white" data-testid="publications-page">
      {/* ============= HERO ============= */}
      <section className="relative bg-[#0a0f1e] pt-32 pb-24 overflow-hidden">
        <div className="absolute inset-0 opacity-[0.10] bg-[linear-gradient(to_right,#ffffff18_1px,transparent_1px),linear-gradient(to_bottom,#ffffff18_1px,transparent_1px)] bg-[size:48px_48px] pointer-events-none" />
        <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-brand-yellow/15 blur-[160px] rounded-full pointer-events-none" />

        <div className="relative max-w-[1400px] mx-auto px-[7.6923%]">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
            <div className="lg:col-span-8">
              <div className="inline-flex items-center gap-2 border border-brand-yellow/40 bg-brand-yellow/10 px-3 py-1.5 mb-6">
                <BookOpen size={14} className="text-brand-yellow" />
                <span className="font-mono text-[10px] tracking-[0.25em] text-brand-yellow uppercase">
                  Publications · RST &amp; Co.
                </span>
              </div>
              <h1 className="font-heading text-5xl md:text-6xl lg:text-7xl font-black text-white leading-[1.02] tracking-tight mb-6">
                The RST Compliance
                <br />
                <span className="text-brand-yellow">Manual.</span>
              </h1>
              <p className="text-lg md:text-xl text-white/70 leading-relaxed max-w-2xl">
                Our flagship publication — a practical, regulator-mapped reference for Indian
                business entities. Curated by the partners and updated for every Finance Act, GST
                Council decision, and regulatory shift.
              </p>
            </div>

            <div className="lg:col-span-4">
              <div className="border-l-2 border-brand-yellow pl-5 space-y-3">
                <div className="font-mono text-[10px] tracking-[0.25em] text-brand-yellow uppercase">
                  At a glance
                </div>
                <div className="flex justify-between items-baseline border-b border-white/10 pb-2 text-white/80">
                  <span className="text-sm">Chapters</span>
                  <span className="font-heading text-2xl font-bold text-white">
                    {COMPLIANCE_HEADS.length}
                  </span>
                </div>
                <div className="flex justify-between items-baseline border-b border-white/10 pb-2 text-white/80">
                  <span className="text-sm">Pages (2024)</span>
                  <span className="font-heading text-2xl font-bold text-white">123</span>
                </div>
                <div className="flex justify-between items-baseline border-b border-white/10 pb-2 text-white/80">
                  <span className="text-sm">Editions</span>
                  <span className="font-heading text-2xl font-bold text-white">2024 · 2026 · 2027</span>
                </div>
                <div className="flex justify-between items-baseline text-white/80">
                  <span className="text-sm">Format</span>
                  <span className="font-heading text-2xl font-bold text-white">PDF</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============= EDITION TABS ============= */}
      <section className="sticky top-28 z-40 bg-white/95 backdrop-blur-xl border-b border-gray-200">
        <div className="max-w-[1400px] mx-auto px-[7.6923%]">
          <div
            className="flex items-center gap-2 overflow-x-auto py-4 no-scrollbar"
            data-testid="edition-tabs"
          >
            <div className="font-mono text-[10px] tracking-[0.25em] text-gray-500 uppercase mr-3 hidden md:block">
              Compliance Manual · Edition:
            </div>
            {EDITIONS.map((ed) => {
              const isActive = activeEdition === ed.year;
              return (
                <button
                  key={ed.year}
                  onClick={() => setActiveEdition(ed.year)}
                  className={`flex items-center gap-3 px-5 py-2.5 whitespace-nowrap transition-all duration-300 border ${
                    isActive
                      ? 'bg-gray-900 text-white border-gray-900'
                      : 'bg-white text-gray-700 border-gray-200 hover:border-gray-900'
                  }`}
                  data-testid={`edition-tab-${ed.year}`}
                >
                  <Calendar
                    size={14}
                    className={isActive ? 'text-brand-yellow' : 'text-gray-400'}
                  />
                  <span className="text-sm font-medium">{ed.year}</span>
                  {ed.tone === 'released' && (
                    <span className="text-[9px] font-mono tracking-widest px-1.5 py-0.5 bg-green-500/15 text-green-600 border border-green-500/30 uppercase">
                      Live
                    </span>
                  )}
                  {ed.tone === 'updated' && (
                    <span className="text-[9px] font-mono tracking-widest px-1.5 py-0.5 bg-brand-yellow/20 text-yellow-700 border border-brand-yellow/40 uppercase">
                      Update
                    </span>
                  )}
                  {ed.tone === 'forthcoming' && (
                    <span className="text-[9px] font-mono tracking-widest px-1.5 py-0.5 bg-brand-yellow/20 text-brand-yellow border border-brand-yellow/30 uppercase">
                      Soon
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      </section>

      {/* ============= ACTIVE EDITION SUMMARY ============= */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-[1400px] mx-auto px-[7.6923%]">
          {currentEdition && (
            <div className="bg-[#0a0f1e] text-white p-10 md:p-12 grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
              <div className="lg:col-span-8">
                <div className="font-mono text-[10px] tracking-[0.25em] text-brand-yellow uppercase mb-3">
                  {currentEdition.label}
                </div>
                <h2 className="font-heading text-3xl md:text-4xl font-black leading-tight mb-3">
                  Compliance Manual · {currentEdition.year} Edition
                </h2>
                <p className="text-white/70 text-base md:text-lg max-w-2xl leading-relaxed">
                  {currentEdition.description}
                </p>
              </div>
              <div className="lg:col-span-4 flex flex-col gap-3 lg:items-end">
                {isDownloadable ? (
                  <a
                    href={currentEdition.fileUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center justify-center px-6 py-5 h-auto font-semibold bg-brand-yellow hover:bg-brand-yellow/90 text-gray-900 rounded-none transition-colors"
                    data-testid={`download-full-${currentEdition.year}`}
                  >
                    <Download size={18} className="mr-2" />
                    Open Full PDF · {currentEdition.year}
                  </a>
                ) : (
                  <button
                    type="button"
                    disabled
                    className="inline-flex items-center justify-center px-6 py-5 h-auto font-semibold rounded-none bg-white/10 text-white/40 cursor-not-allowed"
                    data-testid={`download-full-${currentEdition.year}`}
                  >
                    <Download size={18} className="mr-2" />
                    {currentEdition.tone === 'updated'
                      ? 'Coming Soon · Notify Me'
                      : 'Notify Me When Ready'}
                  </button>
                )}
                <span className="text-[11px] text-white/40">
                  {isDownloadable
                    ? '123 pages · Opens in a new tab. For information only.'
                    : currentEdition.tone === 'updated'
                    ? 'Update in progress · Q4 2026'
                    : 'Release planned · 2027'}
                </span>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* ============= COMPLIANCE HEADS GRID ============= */}
      <section className="py-20 bg-white">
        <div className="max-w-[1400px] mx-auto px-[7.6923%]">
          <div className="mb-12">
            <div className="font-mono text-[11px] tracking-[0.25em] text-brand-blue uppercase mb-3">
              Chapter Heads · {activeEdition} Edition
            </div>
            <h2 className="font-heading text-3xl md:text-4xl font-black text-gray-900 tracking-tight leading-tight mb-4">
              Every regulator. Every entity. One manual.
            </h2>
            <p className="text-base md:text-lg text-gray-600 max-w-3xl leading-relaxed">
              Designed for entrepreneurs, founders, finance heads and compliance teams — applicable
              across individuals, HUFs, firms, LLPs, companies, trusts, societies, start-ups,
              co-operatives and foreign companies.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-gray-200 border border-gray-200">
            {COMPLIANCE_HEADS.map((h) => {
              const Icon = h.icon;
              // New (DPDPA / AI) head: not in any current PDF — show "Added in 2026"
              const headNotInThisEdition = h.isNew && activeEdition === '2024';
              const editionIsLive = activeEdition === '2024' && !headNotInThisEdition && Boolean(h.file);
              return (
                <div
                  key={h.id}
                  className="bg-white p-7 hover:bg-gray-50 transition-colors duration-300 group flex flex-col"
                  data-testid={`head-${h.id}`}
                >
                  <div className="flex items-start justify-between mb-5">
                    <div className="w-12 h-12 flex items-center justify-center bg-brand-blue/10 group-hover:bg-brand-blue group-hover:text-white transition-colors">
                      <Icon
                        className="text-brand-blue group-hover:text-white"
                        size={22}
                        strokeWidth={1.75}
                      />
                    </div>
                    <div className="flex items-center gap-2">
                      {h.isNew && (
                        <span className="text-[9px] font-mono tracking-widest px-2 py-0.5 bg-brand-yellow text-gray-900 font-bold uppercase">
                          New · 2026
                        </span>
                      )}
                      <span className="font-mono text-[10px] tracking-[0.2em] text-gray-400">
                        {h.no}
                      </span>
                    </div>
                  </div>
                  <h3 className="font-heading text-lg font-bold text-gray-900 mb-2 leading-tight">
                    {h.title}
                  </h3>
                  <p className="text-sm text-gray-600 leading-relaxed mb-5 flex-grow">
                    {h.summary}
                  </p>
                  <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                    <span className="inline-flex items-center gap-1.5 text-[11px] font-mono tracking-wider text-gray-500 uppercase">
                      <Tag size={12} />
                      Edition {activeEdition}
                    </span>
                    {editionIsLive ? (
                      <a
                        href={h.file}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1.5 text-xs font-semibold text-brand-blue hover:text-gray-900 transition-colors"
                        data-testid={`download-${h.id}-${activeEdition}`}
                        title="Opens chapter PDF in a new tab"
                      >
                        Open Chapter PDF
                        <ArrowUpRight size={13} />
                      </a>
                    ) : (
                      <span className="inline-flex items-center gap-1.5 text-xs font-medium text-gray-400">
                        {headNotInThisEdition ? 'Added in 2026' : 'Coming soon'}
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          <div className="mt-12 bg-gray-50 border border-gray-200 p-8 flex flex-col md:flex-row md:items-center md:justify-between gap-6">
            <div>
              <h3 className="font-heading text-xl font-bold text-gray-900 mb-1">
                Need a tailored compliance walk-through?
              </h3>
              <p className="text-sm text-gray-600">
                Our partners present the manual on-site for your finance, legal, and audit teams.
              </p>
            </div>
            <Button
              onClick={goToContact}
              className="bg-gray-900 hover:bg-gray-800 text-white rounded-none px-6 py-5 h-auto group"
              data-testid="publications-talk-cta"
            >
              Request a Briefing
              <ArrowRight size={18} className="ml-2 group-hover:translate-x-1 transition-transform" />
            </Button>
          </div>
        </div>
      </section>

      {/* ============= UPCOMING PUBLICATIONS TEASE ============= */}
      <section className="py-20 bg-[#0a0f1e] text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-[0.08] bg-[linear-gradient(to_right,#ffffff22_1px,transparent_1px),linear-gradient(to_bottom,#ffffff22_1px,transparent_1px)] bg-[size:48px_48px]" />
        <div className="absolute -top-40 left-1/2 -translate-x-1/2 w-[800px] h-[400px] bg-brand-yellow/10 blur-[160px] rounded-full" />
        <div className="relative max-w-[1400px] mx-auto px-[7.6923%]">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-8">
              <div className="font-mono text-[10px] tracking-[0.25em] text-brand-yellow uppercase mb-4">
                Coming next
              </div>
              <h2 className="font-heading text-3xl md:text-5xl font-black leading-tight tracking-tight mb-4">
                Quarterly AI Insights brief · GST 2.0 playbook ·
                <br />
                NRI Tax Handbook 2027
              </h2>
              <p className="text-white/70 text-base md:text-lg max-w-2xl leading-relaxed">
                More publications from the partners and the AI Insights desk — covering tax,
                transactions, risk, technology, and managed-services topics.
              </p>
            </div>
            <div className="lg:col-span-4 lg:justify-self-end">
              <Button
                onClick={goToContact}
                className="bg-brand-yellow hover:bg-brand-yellow/90 text-gray-900 font-semibold px-7 py-5 h-auto rounded-none group"
                data-testid="publications-notify-cta"
              >
                Subscribe for updates
                <ArrowUpRight
                  size={18}
                  className="ml-2 group-hover:rotate-45 transition-transform"
                />
              </Button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

// Silence unused-icon lint warnings (icons reserved for future heads / edition cards)
const _reserved = { Landmark, Users, Shield, FileText };
void _reserved;

export default PublicationsPage;
